import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Square, Maximize2, Minimize2 } from "lucide-react";
import Layout from "../components/Layout";
import VoiceToggle from "../components/VoiceToggle";
import VoiceHelp from "../components/VoiceHelp";
import SessionResult from "../components/SessionResult";
import useFitText from "../hooks/useFitText";
import {
  useSpeechRecognition,
  getSRLang,
} from "../hooks/useSpeechRecognition";
import { parseNumbers } from "../lib/voiceParsers";
import { useLanguage } from "../i18n/LanguageContext";

function genProblem(prev) {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  const problem = { a, b, answer: a + b };
  if (prev && prev.a === a && prev.b === b) return genProblem(prev);
  return problem;
}

export default function MathTrainer() {
  const { t, lang } = useLanguage();
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [intervalMs, setIntervalMs] = useState(3000);
  const [tasks, setTasks] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [voice, setVoice] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [result, setResult] = useState(null);
  const intervalRef = useRef(null);
  const stageWrapRef = useRef(null);
  const currentRef = useRef(null);
  const answeredRef = useRef(false);
  const tasksRef = useRef(0);
  const correctRef = useRef(0);
  const runningRef = useRef(false);

  const { containerRef, textRef } = useFitText([current, isFs]);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);
  useEffect(() => {
    correctRef.current = correct;
  }, [correct]);
  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  const handleStart = useCallback(() => {
    if (runningRef.current) return;
    setResult(null);
    setRunning(true);
  }, []);
  const handleStop = useCallback(() => {
    if (!runningRef.current) return;
    setResult({ tasks: tasksRef.current, correct: correctRef.current });
    setRunning(false);
  }, []);
  const handleToggle = useCallback(() => {
    if (runningRef.current) handleStop();
    else handleStart();
  }, [handleStart, handleStop]);

  const onResult = useCallback(({ words }) => {
    if (!currentRef.current || answeredRef.current) return;
    // Compare against ALL numbers heard so a player can say
    // "five plus five equals ten" — the answer is anywhere in the phrase.
    const heard = parseNumbers(words.join(" "), lang);
    if (heard.includes(currentRef.current.answer)) {
      answeredRef.current = true;
      setCorrect((c) => c + 1);
    }
  }, [lang]);

  const { supported: voiceSupported, listening } = useSpeechRecognition({
    enabled: voice,
    lang: getSRLang(lang),
    onStart: handleStart,
    onStop: handleStop,
    onResult,
  });

  useEffect(() => {
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    setTasks(0);
    setCorrect(0);
    answeredRef.current = false;
    let prev = null;
    const fire = () => {
      const next = genProblem(prev);
      prev = next;
      setCurrent(next);
      setTasks((c) => c + 1);
      answeredRef.current = false;
    };
    fire();
    intervalRef.current = setInterval(fire, intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, intervalMs]);

  const toggleFs = async () => {
    const el = stageWrapRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setIsFs(true);
      } else {
        await document.exitFullscreen();
        setIsFs(false);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <Layout showBack>
      <div className="vls-page vls-page-trainer" data-testid="math-page">
        {!running && (
          <h1 className="vls-page-title">
            <span className="vls-grad-orange">{t("math")}</span>
          </h1>
        )}

        <div
          ref={stageWrapRef}
          className="vls-color-stage vls-stage-fit vls-stage-math"
          data-testid="math-stage"
        >
          <div className="vls-fit-box" ref={containerRef}>
            {running && current ? (
              <span
                ref={textRef}
                className="vls-fit-text vls-fit-math"
                data-testid="math-problem"
              >
                <span className="vls-math-num">{current.a}</span>
                <span className="vls-math-op">+</span>
                <span className="vls-math-num">{current.b}</span>
                <span className="vls-math-op">=</span>
              </span>
            ) : (
              <div className="vls-stage-placeholder">
                <p>{t("tapToStart")}</p>
                {voiceSupported && (
                  <p className="vls-stage-hint">{t("voiceHint")}</p>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="vls-fs-btn"
            onClick={toggleFs}
            aria-label={isFs ? t("exitFullscreen") : t("fullscreen")}
            data-testid="fullscreen-button"
          >
            {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>

        {!running && (
          <div className="vls-interval-row">
            <label className="vls-interval-label" htmlFor="interval-slider">
              {t("interval")}:{" "}
              <strong data-testid="interval-value">
                {(intervalMs / 1000).toFixed(1)}s
              </strong>
            </label>
            <input
              id="interval-slider"
              type="range"
              min="1000"
              max="6000"
              step="500"
              value={intervalMs}
              onChange={(e) => setIntervalMs(Number(e.target.value))}
              data-testid="interval-slider"
            />
          </div>
        )}

        <div className="vls-stats">
          <div className="vls-stat">
            <span className="vls-stat-label">{t("tasksLabel")}</span>
            <span className="vls-stat-value" data-testid="stat-tasks">{tasks}</span>
          </div>
          <div className="vls-stat">
            <span className="vls-stat-label">{t("correctLabel")}</span>
            <span
              className="vls-stat-value vls-grad-orange"
              data-testid="stat-correct"
            >
              {correct}
            </span>
          </div>
        </div>

        <div className="vls-controls">
          <button
            type="button"
            className={`vls-btn-primary ${running ? "stop" : ""}`}
            onClick={handleToggle}
            data-testid="start-stop-button"
          >
            {running ? <Square size={22} /> : <Play size={22} />}
            <span>{running ? t("stop") : t("start")}</span>
          </button>
          <VoiceToggle
            enabled={voice}
            setEnabled={setVoice}
            supported={voiceSupported}
            listening={listening}
          />
        </div>

        {!running && (
          <VoiceHelp answerHintKey="voiceCmdMath" supported={voiceSupported} />
        )}

        {result && (
          <SessionResult
            tasks={result.tasks}
            correct={result.correct}
            onClose={() => setResult(null)}
            onRetry={() => {
              setResult(null);
              handleStart();
            }}
          />
        )}
      </div>
    </Layout>
  );
}
